/* eslint-disable brace-style */
import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, inject } from '@angular/core';

import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  AbstractFormDialog,
  AbstractFormDialogData,
  AbstractDialogResultData,
} from '@next/core-lib/dialog';
import { Observable, of, ReplaySubject } from 'rxjs';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';

import {
  CropAid,
  NutritionalCompositionData,
  ProprietaryFertilizerData,
} from '../../models/proprietary-fertilizer.model';

import { ProprietaryFertilizerFormService } from '../../services/proprietary-fertilizer-form.service';

export interface ProprietaryFertilizerDialogModel extends AbstractFormDialogData {
  id: number;
  originalCropAidId: number;
  name: string;
  expirationCropYear?: number;
  nutritionalComposition: NutritionalCompositionData;
  cropAids: CropAid[];
  cropYear: number;
}

@Component({
  selector: 'app-proprietary-fertilizer-dialog',
  templateUrl: './proprietary-fertilizer-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProprietaryFertilizerDialogComponent
  extends AbstractFormDialog<ProprietaryFertilizerData>
  implements OnInit, OnDestroy
{
  data = inject<ProprietaryFertilizerDialogModel>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef =
    inject<
      MatDialogRef<
        ProprietaryFertilizerDialogComponent,
        AbstractDialogResultData<ProprietaryFertilizerData>
      >
    >(MatDialogRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly proprietaryFertilizerFormService = inject(ProprietaryFertilizerFormService);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  isPreviousCropYear = this.data.cropYear < new Date().getFullYear();
  isCreateMode = this.data.id === -1;
  isPreviousYearCreate = this.isPreviousCropYear && this.isCreateMode;
  isOriginalCropAidDisabled = this.isPreviousYearCreate || !!this.data.originalCropAidId;

  nutritionalCompositionFormGroup = this.formBuilder.group({
    n: [
      { value: this.data.nutritionalComposition.n, disabled: this.isPreviousYearCreate },
      [Validators.min(0), Validators.max(100)],
    ],
    p2O5: [
      { value: this.data.nutritionalComposition.p2O5, disabled: this.isPreviousYearCreate },
      [Validators.min(0), Validators.max(100)],
    ],
    k2O: [
      { value: this.data.nutritionalComposition.k2O, disabled: this.isPreviousYearCreate },
      [Validators.min(0), Validators.max(100)],
    ],
    na2O: [
      { value: this.data.nutritionalComposition.na2O, disabled: this.isPreviousYearCreate },
      [Validators.min(0), Validators.max(100)],
    ],
    mgO: [
      { value: this.data.nutritionalComposition.mgO, disabled: this.isPreviousYearCreate },
      [Validators.min(0), Validators.max(100)],
    ],
    caO: [
      { value: this.data.nutritionalComposition.caO, disabled: this.isPreviousYearCreate },
      [Validators.min(0), Validators.max(100)],
    ],
    sO3: [
      { value: this.data.nutritionalComposition.sO3, disabled: this.isPreviousYearCreate },
      [Validators.min(0), Validators.max(100)],
    ],
    b: [
      { value: this.data.nutritionalComposition.b, disabled: this.isPreviousYearCreate },
      [Validators.min(0), Validators.max(100)],
    ],
    mn: [
      { value: this.data.nutritionalComposition.mn, disabled: this.isPreviousYearCreate },
      [Validators.min(0), Validators.max(100)],
    ],
    fe: [
      { value: this.data.nutritionalComposition.fe, disabled: this.isPreviousYearCreate },
      [Validators.min(0), Validators.max(100)],
    ],
    zn: [
      { value: this.data.nutritionalComposition.zn, disabled: this.isPreviousYearCreate },
      [Validators.min(0), Validators.max(100)],
    ],
    cu: [
      { value: this.data.nutritionalComposition.cu, disabled: this.isPreviousYearCreate },
      [Validators.min(0), Validators.max(100)],
    ],
    co: [
      { value: this.data.nutritionalComposition.co, disabled: this.isPreviousYearCreate },
      [Validators.min(0), Validators.max(100)],
    ],
  });
  proprietaryCustomerInformationFormGroup = this.formBuilder.group({});

  formGroup = this.formBuilder.group({
    id: [this.data.id],
    originalCropAidId: [
      {
        value: this.data.originalCropAidId || '',
        disabled: this.isOriginalCropAidDisabled,
      },
      [Validators.required],
    ],
    name: [
      {
        value: this.data.name,
        disabled: this.isPreviousYearCreate,
      },
      [Validators.required, Validators.minLength(1), Validators.maxLength(100)],
    ],
    expirationCropYear: [
      {
        value: this.data.expirationCropYear ?? 0,
        disabled: this.isPreviousYearCreate,
      },
    ],
    nutritionalComposition: this.nutritionalCompositionFormGroup,
  });

  filteredCropAids$ = this.formGroup.controls.originalCropAidId.valueChanges.pipe(
    startWith(''),
    filter((value) => typeof value === 'string'),
    map((value: string) => this.filter(value, this.data.cropAids)),
  );

  selectedCropAid$ = this.formGroup.controls.originalCropAidId.valueChanges.pipe(
    filter((value) => typeof value === 'number'),
    map((cropAidId) => this.data.cropAids.find((item) => item.id === cropAidId)),
  );

  expirationCropYears$: Observable<number[]>;

  ngOnInit() {
    this.data.cropAids = this.data.cropAids.filter((cropAid) => !cropAid.isHidden);
    this.selectedCropAid$.pipe(takeUntil(this.destroyed$)).subscribe((cropAid: CropAid) => {
      this.updateNutrientValues(cropAid);
    });

    const expirationCropYears = Array.from({ length: 100 }, (_, i) => this.data.cropYear + i);
    this.expirationCropYears$ = of(expirationCropYears);
  }

  updateNutrientValues(cropAid: CropAid) {
    const nutrionalData = cropAid?.fertilizer?.nutritionalComposition || {};
    const formGroup = this.formGroup.get('nutritionalComposition');
    Object.keys(nutrionalData).forEach((nutrientKey) => {
      const newValue = nutrionalData[nutrientKey];
      const formattedValue = !newValue
        ? null
        : this.proprietaryFertilizerFormService.getNutritionalCompositionFormValue(
            newValue,
            cropAid.unitOfMeasurement,
            cropAid.fertilizer.density,
          );
      formGroup.get(nutrientKey).setValue(formattedValue);
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  confirm() {
    this.checkOriginalCropAid();

    if (this.formGroup.valid) {
      const proprietaryFertilizer = this.formGroup.getRawValue() as ProprietaryFertilizerData;
      const result = {
        ...proprietaryFertilizer,
        expirationCropYear:
          proprietaryFertilizer.expirationCropYear !== 0
            ? proprietaryFertilizer.expirationCropYear
            : null,
      };
      super.confirm(result);
    }
  }

  displayCropAid() {
    return (cropAidId?: number) =>
      cropAidId && this.data.cropAids.length
        ? this.data.cropAids.find((cropAid) => cropAid.id === cropAidId)?.name
        : null;
  }

  checkOriginalCropAid() {
    if (typeof this.formGroup.controls.originalCropAidId.value === 'string') {
      this.formGroup.controls.originalCropAidId.setValue(null);
    }
  }

  private filter(value: string, cropAids: CropAid[]) {
    return cropAids
      .sort((a, b) =>
        !(a.name && b.name) ? 0 : a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
      )
      .filter((option) =>
        !option.name ? false : option.name.toLowerCase().includes(value.toLowerCase()),
      );
  }
}
